import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const Waitlist = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    referralCode: '',
    useCase: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [debugInfo, setDebugInfo] = useState(null);

  // Clear debug info after 10 seconds
  useEffect(() => {
    if (debugInfo) {
      const timer = setTimeout(() => {
        setDebugInfo(null);
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [debugInfo]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    setDebugInfo(null);
    
    try {
      console.log('Submitting form data:', formData);
      
      // Form validation
      if (!formData.name || !formData.email || !formData.useCase) {
        throw new Error('Please fill in all required fields: name, email, and why you are interested.');
      }
      
      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        throw new Error('Please enter a valid email address.');
      }
      
      // Submit to the actual API endpoint
      // In production (Vercel), API routes are relative to the domain
      // In development, we use the REACT_APP_API_URL environment variable
      const apiUrl = process.env.NODE_ENV === 'production' 
        ? window.location.origin 
        : (process.env.REACT_APP_API_URL || '');
      const endpoint = `${apiUrl}/api/submit-waitlist`;
      console.log('Submitting to API endpoint:', endpoint);
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });
      
      // Clone the response before reading to prevent "body stream already read" errors
      const responseClone = response.clone();
      
      // Try to parse the JSON response
      let result;
      try {
        result = await response.json();
      } catch (err) {
        // If JSON parsing fails, get the text response for debugging
        const textResponse = await responseClone.text();
        console.error('Failed to parse JSON response:', textResponse);
        throw new Error('Invalid response from server');
      }
      
      // Check if the request was successful
      if (!response.ok) {
        throw new Error(result.error || 'Failed to submit your request');
      }
      
      console.log('Form submission result:', result);
      
      // Show success message
      setIsSubmitted(true);
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        company: '',
        referralCode: '',
        useCase: ''
      });
      
    } catch (err) {
      console.error('Error submitting form:', err);
      setError(err.message || 'Failed to submit your request. Please try again later.');
      
      // Capture debug info
      setDebugInfo({
        timestamp: new Date().toISOString(),
        error: err.toString(),
        stack: err.stack,
        formData: { ...formData, useCase: formData.useCase.substring(0, 20) + '...' } // Truncate for privacy
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="waitlist" className="min-h-screen flex items-center bg-background">
      <div className="container mx-auto px-4 md:px-6 py-16 pt-28">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold mb-4">Be Among the First</h2>
          <p className="text-lightText max-w-2xl mx-auto">
            We're shaping the future of intelligent experiences. Join our early access program and be part of something groundbreaking.
          </p>
        </div>
        
        <div className="max-w-2xl mx-auto bg-cardBg p-8 rounded-lg border border-purple-500 border-opacity-10">
          {isSubmitted ? (
            <div className="text-center py-8 bg-gradient-to-br from-gradientStart to-gradientEnd rounded-lg">
              <h3 className="text-2xl font-bold mb-3">Thank You for Your Interest</h3>
              <p className="text-lightText mb-6">
                We've received your request and will get back to you within 5 business days. Our team is reviewing all submissions to provide the best experience for our early access partners.
              </p>
              <button 
                onClick={() => setIsSubmitted(false)}
                className="bg-purple-500 text-white px-6 py-2 rounded-md font-medium hover:bg-opacity-90 transition-all duration-300"
              >
                Back to Home
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
                  <span className="block sm:inline">{error}</span>
                </div>
              )}
              
              {debugInfo && (
                <div className="bg-gray-100 border border-gray-400 text-gray-700 px-4 py-3 rounded relative text-xs overflow-auto max-h-32">
                  <strong>Debug Info:</strong>
                  <pre>{JSON.stringify(debugInfo, null, 2)}</pre>
                </div>
              )}
              
              <div>
                <label htmlFor="name" className="block text-sm font-medium mb-2">Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full bg-secondary bg-opacity-50 border border-purple-500 border-opacity-20 rounded-md px-4 py-2 text-textColor focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label htmlFor="email" className="block text-sm font-medium mb-2">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full bg-secondary bg-opacity-50 border border-purple-500 border-opacity-20 rounded-md px-4 py-2 text-textColor focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label htmlFor="company" className="block text-sm font-medium mb-2">Company</label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full bg-secondary bg-opacity-50 border border-purple-500 border-opacity-20 rounded-md px-4 py-2 text-textColor focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label htmlFor="referralCode" className="block text-sm font-medium mb-2">Referral Code (Optional)</label>
                <input
                  type="text"
                  id="referralCode"
                  name="referralCode"
                  value={formData.referralCode}
                  onChange={handleChange}
                  className="w-full bg-secondary bg-opacity-50 border border-purple-500 border-opacity-20 rounded-md px-4 py-2 text-textColor focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <p className="text-sm text-accent mt-1 italic">Having a referral code significantly improves your position on the waitlist</p>
              </div>
              
              <div>
                <label htmlFor="useCase" className="block text-sm font-medium mb-2">Why are you interested in Humaniti.ai?</label>
                <textarea
                  id="useCase"
                  name="useCase"
                  value={formData.useCase}
                  onChange={handleChange}
                  required
                  rows="6"
                  className="w-full bg-secondary bg-opacity-50 border border-purple-500 border-opacity-20 rounded-md px-4 py-2 text-textColor focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                ></textarea>
              </div>
              
              <div>
                <button 
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-purple-500 text-white px-6 py-3 rounded-md font-medium hover:bg-opacity-90 transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Submitting...' : 'Request Early Access'}
                </button>
              </div>
              
              <div className="text-center text-sm text-lightText">
                By joining, you agree to our <Link to="/privacy-policy" className="text-purple-500 hover:underline">Privacy Policy</Link>.
              </div>
            </form>
          )}
        </div>
        
        <div className="mt-12 text-center text-sm text-lightText">
          <p className="max-w-md mx-auto border-t border-purple-500 border-opacity-20 pt-4">Limited spots available. Applications are reviewed based on use case fit and potential for partnership.</p>
        </div>
      </div>
    </section>
  );
};

export default Waitlist;
